/**
 * Handlers de l'éditeur avancé d'exercice : enregistrement, annulation,
 * aperçu et export IA (extrait de ExerciceAdvancedEditor).
 */
import { useState, useCallback } from 'react';
import { NavigateFunction } from 'react-router-dom';
import ExercicesApiService from '../../../services/exercicesApi';
import { Exercice } from '../../../types';
import { generateAIPrompt, ExerciceContext } from '../../../utils/exerciceAIExport';
import { AppEnvironment } from '../../../config/environment';
import { Notifier } from '../../../contexts/NotificationContext';
import { logger } from '../../../lib/logger';
import { adresseExplorateur } from '../../../features/contenu/adresses';

interface EditorHandlersParams {
  id: string | undefined;
  environment: AppEnvironment;
  navigate: NavigateFunction;
  notify: Notifier['notify'];
  setError: (error: string | null) => void;
  setPreviewData: (data: Omit<Exercice, 'id'> | null) => void;
  loadExercice: () => Promise<void>;
}

/** État « sauvegarde » et handlers du formulaire. */
export function useEditorHandlers({
  id, environment, navigate, notify, setError, setPreviewData, loadExercice,
}: EditorHandlersParams) {
  const [saving, setSaving] = useState(false);

  const handleFormChange = (updatedData: Omit<Exercice, 'id'>) => {
    // Mettre à jour le preview en temps réel
    setPreviewData(updatedData);
  };

  const handleSubmit = async (data: Omit<Exercice, 'id'>) => {
    if (!id) return;

    try {
      setSaving(true);
      await ExercicesApiService.updateExercice(environment, id, data);
      // Afficher un message de succès et recharger les données
      notify('Exercice mis à jour avec succès !', 'success');
      // Recharger les données de l'exercice
      await loadExercice();
    } catch (err) {
      logger.error('Erreur lors de la sauvegarde:', err);
      setError('Erreur lors de la sauvegarde de l\'exercice');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(adresseExplorateur({ onglet: 'exercices' }));
  };

  return { saving, handleFormChange, handleSubmit, handleCancel };
}

interface ExportForAIParams {
  exercice: Exercice | null;
  matiereNom: string;
  leconTitre: string;
  chapitreTitre: string;
  notify: Notifier['notify'];
}

/**
 * Export l'exercice pour correction IA
 * Copie le prompt + JSON dans le presse-papier
 */
export function useExportForAI({ exercice, matiereNom, leconTitre, chapitreTitre, notify }: ExportForAIParams) {
  return useCallback(async () => {
    if (!exercice) return;

    const context: ExerciceContext = {
      matiereName: matiereNom || 'Non spécifiée',
      leconName: leconTitre || 'Non spécifiée',
      chapitreName: chapitreTitre || 'Non spécifié'
    };

    const prompt = generateAIPrompt(exercice, context);

    try {
      await navigator.clipboard.writeText(prompt);
      notify('Exercice copié dans le presse-papier', 'success');
    } catch (err) {
      logger.error('Erreur copie presse-papier:', err);
      notify('Erreur lors de la copie dans le presse-papier', 'error');
    }
  }, [exercice, matiereNom, leconTitre, chapitreTitre, notify]);
}
